// src/admin/pages/review/reviewService.ts
import {
  collection,
  doc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

const REVIEWS_COLLECTION = 'reviews';

export interface Review {
  id?: string;
  comment: string;
  createdAt: Date;
  updatedAt: Date;
  imageUrl: string;
  productId: string;
  productName: string;
  rating: number;
  userEmail: string;
  userId: string;
  userName: string;
}

export interface PaginatedReviewsResult {
  reviews: Review[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export class ReviewService {
  /**
   * Fetch paginated reviews with optional filters.
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * Also fixed the broken count query — the original used
   * `queryConstraints.filter(c => c.type === 'where')` which always returned
   * an empty array (Firestore constraint objects have no `.type` property),
   * so getCountFromServer was always returning the unfiltered total count
   * regardless of the active rating filter.
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   */
  static async getReviewsPaginated(
    pageSize: number = 10,
    page: number = 1,
    ratingFilter?: number,
    searchQuery?: string
  ): Promise<PaginatedReviewsResult> {
    try {
      const reviews: Review[] = [];
      const collectionRef = collection(db, REVIEWS_COLLECTION);

      // Build where-constraints (shared between count + seek + page queries)
      const whereConstraints: any[] = [];
      if (ratingFilter !== undefined) {
        whereConstraints.push(where('rating', '==', ratingFilter));
      }

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) — FIX: pass whereConstraints directly ──
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...whereConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...whereConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...whereConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...whereConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const review: Review = {
          id: doc.id,
          comment: data.comment || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          imageUrl: data.imageUrl || '',
          productId: data.productId || '',
          productName: data.productName || '',
          rating: data.rating || 0,
          userEmail: data.userEmail || '',
          userId: data.userId || '',
          userName: data.userName || '',
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesSearch =
            review.userName.toLowerCase().includes(searchLower) ||
            review.userEmail.toLowerCase().includes(searchLower) ||
            review.productName.toLowerCase().includes(searchLower) ||
            review.productId.toLowerCase().includes(searchLower) ||
            review.comment.toLowerCase().includes(searchLower);

          if (matchesSearch) reviews.push(review);
        } else {
          reviews.push(review);
        }
      });

      return {
        reviews,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error: any) {
      console.error('Error fetching paginated reviews:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use getReviewsPaginated instead
   */
  static async getReviews(): Promise<Review[]> {
    try {
      const reviewsRef = collection(db, REVIEWS_COLLECTION);
      const q = query(reviewsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        comment: doc.data().comment || '',
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        imageUrl: doc.data().imageUrl || '',
        productId: doc.data().productId || '',
        productName: doc.data().productName || '',
        rating: doc.data().rating || 0,
        userEmail: doc.data().userEmail || '',
        userId: doc.data().userId || '',
        userName: doc.data().userName || '',
      })) as Review[];
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
  }

  static async getReview(reviewId: string): Promise<Review | null> {
    try {
      const reviewRef = doc(db, REVIEWS_COLLECTION, reviewId);
      const reviewSnap = await getDoc(reviewRef);

      if (reviewSnap.exists()) {
        const data = reviewSnap.data();
        return {
          id: reviewSnap.id,
          comment: data.comment || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          imageUrl: data.imageUrl || '',
          productId: data.productId || '',
          productName: data.productName || '',
          rating: data.rating || 0,
          userEmail: data.userEmail || '',
          userId: data.userId || '',
          userName: data.userName || '',
        } as Review;
      }

      return null;
    } catch (error) {
      console.error('Error fetching review:', error);
      throw error;
    }
  }

  static async deleteReview(reviewId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, REVIEWS_COLLECTION, reviewId));
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  }

  static async getReviewStats(): Promise<{
    total: number;
    averageRating: number;
    ratingDistribution: { 1: number; 2: number; 3: number; 4: number; 5: number };
  }> {
    try {
      const col = collection(db, REVIEWS_COLLECTION);

      const [totalSnap, r1Snap, r2Snap, r3Snap, r4Snap, r5Snap] = await Promise.all([
        getCountFromServer(query(col)),
        getCountFromServer(query(col, where('rating', '==', 1))),
        getCountFromServer(query(col, where('rating', '==', 2))),
        getCountFromServer(query(col, where('rating', '==', 3))),
        getCountFromServer(query(col, where('rating', '==', 4))),
        getCountFromServer(query(col, where('rating', '==', 5))),
      ]);

      const total = totalSnap.data().count;
      const ratingDistribution = {
        1: r1Snap.data().count,
        2: r2Snap.data().count,
        3: r3Snap.data().count,
        4: r4Snap.data().count,
        5: r5Snap.data().count,
      };

      const averageRating = total > 0
        ? (1 * ratingDistribution[1] +
           2 * ratingDistribution[2] +
           3 * ratingDistribution[3] +
           4 * ratingDistribution[4] +
           5 * ratingDistribution[5]) / total
        : 0;

      return { total, averageRating, ratingDistribution };
    } catch (error) {
      console.error('Error getting review stats:', error);
      throw error;
    }
  }

  static async getReviewsByProduct(productId: string): Promise<Review[]> {
    try {
      const reviewsRef = collection(db, REVIEWS_COLLECTION);
      const q = query(reviewsRef, where('productId', '==', productId), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        comment: doc.data().comment || '',
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        imageUrl: doc.data().imageUrl || '',
        productId: doc.data().productId || '',
        productName: doc.data().productName || '',
        rating: doc.data().rating || 0,
        userEmail: doc.data().userEmail || '',
        userId: doc.data().userId || '',
        userName: doc.data().userName || '',
      })) as Review[];
    } catch (error) {
      console.error('Error fetching reviews by product:', error);
      throw error;
    }
  }

  static async getReviewsByRating(rating: number): Promise<Review[]> {
    try {
      const reviewsRef = collection(db, REVIEWS_COLLECTION);
      const q = query(reviewsRef, where('rating', '==', rating), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        comment: doc.data().comment || '',
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        imageUrl: doc.data().imageUrl || '',
        productId: doc.data().productId || '',
        productName: doc.data().productName || '',
        rating: doc.data().rating || 0,
        userEmail: doc.data().userEmail || '',
        userId: doc.data().userId || '',
        userName: doc.data().userName || '',
      })) as Review[];
    } catch (error) {
      console.error('Error fetching reviews by rating:', error);
      throw error;
    }
  }
}